import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, FilterChip, Progress, Textarea } from '@indipia/ui-core';
import { SharePhotoPicker, UNLOAD_PHOTO_RULE, type SharePhotoItem } from '@indipia/shared-features';
import { receiptShareApi } from '../api/receiptShareApi';
import {
  ISSUE_NOTE_MAX,
  ISSUE_TYPE_OPTIONS,
  RECEIPT_TEXT,
  buildReceiptFormData,
  isRetryableForm,
  mapSubmitException,
  mapSubmitSuccess,
  newSubmitUuid,
  validateReceipt,
  type IssueType,
  type QtyChecked,
  type ReceiptView,
  type ReceivedSummary,
} from '../pages/receiptRegister.logic';

/** `accept` 속성값 — 허용 확장자에서 만든다(규칙은 UNLOAD_PHOTO_RULE 한 곳). */
const ACCEPT = UNLOAD_PHOTO_RULE.allowedExt.map((e) => `.${e}`).join(',');

interface LocalPhoto {
  key: string;
  file: File;
  previewSrc: string;
}

export interface ReceiptFormProps {
  companyName?: string;
  /** 제출 결과로 다른 화면(공통 오류·전용 안내·완료)으로 전환 */
  onView: (view: ReceiptView) => void;
  onDone: (summary: ReceivedSummary) => void;
}

/**
 * 입고 폼(C-4-2, §10.4). 하차사진은 제출 때 한 번에 전송한다(업로드 큐 없음).
 * submitUuid 는 화면 수명 동안 1회 발급해 재시도에서도 바꾸지 않는다(§10.5).
 */
export function ReceiptForm({ companyName, onView, onDone }: ReceiptFormProps) {
  const [submitUuid] = useState<string | null>(() => newSubmitUuid());
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const [qty, setQty] = useState<QtyChecked | null>(null);
  const [issueNote, setIssueNote] = useState('');
  const [issueTypes, setIssueTypes] = useState<IssueType[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const keySeq = useRef(0);
  const inFlight = useRef(false);
  const photosRef = useRef<LocalPhoto[]>([]);
  photosRef.current = photos;
  const abortRef = useRef<AbortController | null>(null);

  // 미리보기 URL 정리(언마운트)
  useEffect(
    () => () => {
      abortRef.current?.abort();
      photosRef.current.forEach((p) => URL.revokeObjectURL(p.previewSrc));
    },
    [],
  );

  // 이탈 보호 — 사진·입력이 있는 미제출 상태와 업로드 중
  const dirty = submitting || photos.length > 0 || qty != null || issueNote.length > 0 || issueTypes.length > 0;
  useEffect(() => {
    if (!dirty) return;
    const onBefore = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [dirty]);

  const files = useMemo(() => photos.map((p) => p.file), [photos]);
  const validation = validateReceipt({ files, qty, issueNote, issueTypes });

  const toggleIssueType = (code: IssueType) => {
    setTypeError(null);
    setIssueTypes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const items: SharePhotoItem[] = useMemo(
    () =>
      photos.map((p, i) => ({
        key: p.key,
        label: `${RECEIPT_TEXT.photoLabel} ${i + 1}`,
        status: 'local' as const,
        previewSrc: p.previewSrc,
      })),
    [photos],
  );

  const handleAdd = useCallback((added: File[]) => {
    setPhotoError(null);
    setNotice(null);
    const next = added.map((file) => ({
      key: `p${++keySeq.current}`,
      file,
      previewSrc: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...next]);
  }, []);

  const handleRemove = useCallback((key: string) => {
    setPhotoError(null);
    setPhotos((prev) => {
      const target = prev.find((p) => p.key === key);
      if (target) URL.revokeObjectURL(target.previewSrc);
      return prev.filter((p) => p.key !== key);
    });
  }, []);

  const handleSubmit = async () => {
    if (inFlight.current || !validation.ready || qty == null || submitUuid == null) return;
    inFlight.current = true;
    setSubmitting(true);
    setProgress(0);
    setFormError(null);
    setRetryable(false);
    setPhotoError(null);
    setNoteError(null);
    setTypeError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await receiptShareApi.submitReceipt(
        buildReceiptFormData(submitUuid, { files, qty, issueNote, issueTypes }),
        setProgress,
        controller.signal,
      );
      const outcome = mapSubmitSuccess(res.data?.data);
      if (outcome.t === 'done') onDone(outcome.summary);
      else if (outcome.t === 'view') onView(outcome.view);
    } catch (e) {
      if (controller.signal.aborted) return;
      const outcome = mapSubmitException(e, companyName);
      if (outcome.t === 'view') {
        onView(outcome.view);
      } else if (outcome.t === 'field') {
        if (outcome.photoError) setPhotoError(outcome.photoError);
        if (outcome.noteError) setNoteError(outcome.noteError);
        if (outcome.typeError) setTypeError(outcome.typeError);
        if (outcome.formError) setFormError(outcome.formError);
      } else if (outcome.t === 'form') {
        setFormError(outcome.message);
        setRetryable(isRetryableForm(outcome));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  // randomUUID 가 없으면 대체 난수를 쓰지 않고 오류 화면으로 보낸다(§10.5).
  useEffect(() => {
    if (submitUuid == null) onView({ t: 'screen', kind: 'UNKNOWN' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noteInvalid = qty === 'ISSUE' && issueNote.trim().length > ISSUE_NOTE_MAX;

  return (
    <form
      className="flex flex-col gap-4"
      aria-busy={submitting || undefined}
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      {formError && (
        <Alert
          kind="error"
          role="alert"
          aria-live="assertive"
          action={
            retryable ? (
              <Button type="button" size="sm" variant="secondary" disabled={submitting} onClick={() => void handleSubmit()}>
                {RECEIPT_TEXT.retry}
              </Button>
            ) : undefined
          }
        >
          {formError}
        </Alert>
      )}

      {/* 하차사진 */}
      <section aria-labelledby="receipt-photo-title" className="flex flex-col gap-2">
        <h2 id="receipt-photo-title" className="text-body-md font-bold text-text-primary">
          {RECEIPT_TEXT.photoLabel} <span className="text-caption text-text-muted">{RECEIPT_TEXT.required}</span>{' '}
          <span className="text-caption font-normal text-text-muted">{RECEIPT_TEXT.photoHint}</span>
        </h2>
        <p id="receipt-photo-desc" className="text-caption text-text-muted">
          {RECEIPT_TEXT.photoRuleDesc}
        </p>
        <SharePhotoPicker
          items={items}
          rule={UNLOAD_PHOTO_RULE}
          accept={ACCEPT}
          disabled={submitting}
          addLabel={RECEIPT_TEXT.photoAddLabel}
          emptyHint={RECEIPT_TEXT.photoEmptyHint}
          onAddFiles={handleAdd}
          onRemove={handleRemove}
          onNotice={setNotice}
          describedBy="receipt-photo-desc"
        />
        <p className="sr-only" aria-live="polite">
          {RECEIPT_TEXT.photoSelectedLive(photos.length)}
        </p>
        {notice && (
          <p className="whitespace-pre-line text-caption text-error" role="alert">
            {notice}
          </p>
        )}
        {photoError && (
          <p className="text-caption text-error" role="alert">
            {photoError}
          </p>
        )}
      </section>

      {/* 수량 확인 */}
      <section aria-labelledby="receipt-qty-title" className="flex flex-col gap-2">
        <h2 id="receipt-qty-title" className="text-body-md font-bold text-text-primary">
          {RECEIPT_TEXT.qtyLabel} <span className="text-caption text-text-muted">{RECEIPT_TEXT.required}</span>
        </h2>
        <div role="group" aria-labelledby="receipt-qty-title" className="flex flex-wrap gap-2">
          <FilterChip
            size="lg"
            active={qty === 'NORMAL'}
            aria-pressed={qty === 'NORMAL'}
            disabled={submitting}
            onClick={() => setQty('NORMAL')}
          >
            {qty === 'NORMAL' ? RECEIPT_TEXT.qtyNormalMark : ''}
            {RECEIPT_TEXT.qtyNormal}
          </FilterChip>
          <FilterChip
            size="lg"
            active={qty === 'ISSUE'}
            aria-pressed={qty === 'ISSUE'}
            disabled={submitting}
            onClick={() => setQty('ISSUE')}
          >
            {qty === 'ISSUE' ? RECEIPT_TEXT.qtyIssueMark : ''}
            {RECEIPT_TEXT.qtyIssueChip}
          </FilterChip>
        </div>
      </section>

      <div aria-live="polite">
        {qty === 'ISSUE' && (
          <div className="flex flex-col gap-2">
            <section aria-labelledby="receipt-issue-type-title" className="flex flex-col gap-2">
              <h2 id="receipt-issue-type-title" className="text-body-md font-bold text-text-primary">
                {RECEIPT_TEXT.issueTypeLabel} <span className="text-caption text-text-muted">{RECEIPT_TEXT.required}</span>{' '}
                <span className="text-caption font-normal text-text-muted">{RECEIPT_TEXT.issueTypeHint}</span>
              </h2>
              <div role="group" aria-labelledby="receipt-issue-type-title" className="flex flex-wrap gap-2">
                {ISSUE_TYPE_OPTIONS.map((opt) => {
                  const active = issueTypes.includes(opt.code);
                  return (
                    <FilterChip
                      key={opt.code}
                      size="lg"
                      active={active}
                      aria-pressed={active}
                      disabled={submitting}
                      onClick={() => toggleIssueType(opt.code)}
                    >
                      {active ? RECEIPT_TEXT.qtyNormalMark : ''}
                      {opt.label}
                    </FilterChip>
                  );
                })}
              </div>
              {typeError && (
                <p className="text-caption text-error" role="alert">
                  {typeError}
                </p>
              )}
            </section>
            <Textarea
              id="receipt-issue-note"
              label={`${RECEIPT_TEXT.issueNoteLabel} (${RECEIPT_TEXT.required})`}
              value={issueNote}
              maxLength={ISSUE_NOTE_MAX}
              placeholder={RECEIPT_TEXT.issueNotePlaceholder}
              disabled={submitting}
              error={noteError ?? (noteInvalid ? RECEIPT_TEXT.errNoteRequired : undefined)}
              onChange={(e) => {
                setIssueNote(e.target.value);
                setNoteError(null);
              }}
            />
            <p className="mt-1 text-right text-caption text-text-muted">
              {issueNote.length}/{ISSUE_NOTE_MAX}
            </p>
            <Alert kind="info">{RECEIPT_TEXT.issueNotifyAlert}</Alert>
          </div>
        )}
      </div>

      {submitting && (
        <div className="flex flex-col gap-1" aria-live="polite">
          <Progress value={progress} />
          <p className="text-caption text-text-muted">{RECEIPT_TEXT.uploadProgress(progress)}</p>
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        variant={validation.variant}
        className="w-full"
        disabled={!validation.ready}
        loading={submitting}
      >
        {validation.buttonLabel}
      </Button>
    </form>
  );
}
